<script setup>
/* global axios */
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFlowBuilderStore } from 'flow_builder/store/index.js';
import { vOnClickOutside } from '@vueuse/components';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import CreateTemplateModal from 'flow_builder/components/CreateTemplateModal.vue';
import TemplateProductPicker from 'flow_builder/components/panels/action-forms/TemplateProductPicker.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import {
  findComponentByType,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox.js';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import {
  FLOW_VARIABLE_LABELS,
  getVariablesForTrigger,
} from 'flow_builder/constants/flowVariables.js';

const props = defineProps({
  actionName: { type: String, default: 'send_message' },
  modelValue: {
    type: Object,
    default: () => ({
      channel_type: null,
      inbox_id: null,
      message_type: 'manual',
      content: '',
      attachments: [],
      buttons: [],
      template: null,
      template_variables: [],
      template_button_variables: {},
    }),
  },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const resolveLabel = label =>
  label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');
const store = useStore();
const { accountId: accountIdForApi } = useAccount();
const flowStore = useFlowBuilderStore();
const flowSlug = computed(() => flowStore.currentFlow?.slug ?? null);
const inboxes = useMapGetter('inboxes/getInboxes');

onMounted(() => store.dispatch('inboxes/get'));

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

// Channel type
const CHANNEL_OPTIONS = [
  { id: INBOX_TYPES.WHATSAPP, name: 'WhatsApp' },
  { id: INBOX_TYPES.EMAIL, name: 'E-mail' },
  { id: INBOX_TYPES.API, name: 'API' },
  { id: INBOX_TYPES.WEB, name: 'Widget' },
  { id: INBOX_TYPES.TELEGRAM, name: 'Telegram' },
  { id: INBOX_TYPES.FB, name: 'Facebook' },
  { id: INBOX_TYPES.INSTAGRAM, name: 'Instagram' },
  { id: INBOX_TYPES.SMS, name: 'SMS' },
];

const selectedChannelType = computed(
  () =>
    CHANNEL_OPTIONS.find(c => c.id === props.modelValue?.channel_type) ?? null
);

function onSelectChannelType(val) {
  update({ channel_type: val?.id ?? null, inbox_id: null });
}

const filteredInboxOptions = computed(() => {
  const all = inboxes.value ?? [];
  const byType = props.modelValue?.channel_type
    ? all.filter(i => i.channel_type === props.modelValue.channel_type)
    : all;
  return byType.map(i => ({ id: i.id, name: i.name }));
});

const selectedInbox = computed(
  () =>
    filteredInboxOptions.value.find(i => i.id === props.modelValue?.inbox_id) ??
    null
);

function onSelectInbox(val) {
  update({ inbox_id: val?.id ?? null });
}

// Message type
const MESSAGE_TYPE_OPTIONS = computed(() => [
  {
    id: 'manual',
    name: t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MESSAGE_TYPE_MANUAL'),
  },
  {
    id: 'template',
    name: t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MESSAGE_TYPE_TEMPLATE'),
  },
]);

const selectedMessageType = computed(
  () =>
    MESSAGE_TYPE_OPTIONS.value.find(
      o => o.id === (props.modelValue?.message_type ?? 'manual')
    ) ?? MESSAGE_TYPE_OPTIONS.value[0]
);

const messageType = computed(() => props.modelValue?.message_type ?? 'manual');

function onSelectMessageType(val) {
  update({
    message_type: val?.id ?? 'manual',
    template: null,
    template_variables: [],
  });
}

// Content (manual)
const content = computed({
  get: () => props.modelValue?.content ?? '',
  set: val => update({ content: val }),
});

// Attachments
const attachments = computed(() => props.modelValue?.attachments ?? []);

function removeAttachment(index) {
  update({ attachments: attachments.value.filter((_, i) => i !== index) });
}

async function handleFileInput(event, type) {
  const file = event.target.files?.[0];
  if (!file) return;
  event.target.value = '';
  try {
    const { fileUrl, blobId } = await uploadFile(file);
    update({
      attachments: [
        ...attachments.value,
        { type, name: file.name, url: fileUrl, blobId },
      ],
    });
  } catch {
    // upload failed silently
  }
}

const FLOW_VARIABLES = {
  'contact.name': '',
  'contact.first_name': '',
  'contact.last_name': '',
  'contact.email': '',
  'contact.phone': '',
  'contact.id': '',
  'conversation.id': '',
  'agent.name': '',
  'agent.email': '',
};

const editorId = 'flow-send-message-editor';
const editorRef = ref(null);
const showEmojiPicker = ref(false);
const showVariablePicker = ref(false);
const variableSearch = ref('');

function onEmojiClick(emoji) {
  content.value = (content.value || '') + emoji;
  showEmojiPicker.value = false;
}

const VARIABLE_LABELS = FLOW_VARIABLE_LABELS;

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

const filteredAttrList = computed(() => {
  const q = variableSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(a =>
    resolveLabel(a.label).toLowerCase().includes(q)
  );
});

function insertVariable(key) {
  editorRef.value?.insertVariableAtCursor(key);
  showVariablePicker.value = false;
  variableSearch.value = '';
}

// Template picker

const allTemplates = computed(() => {
  const inboxId = props.modelValue?.inbox_id;
  if (!inboxId) return [];
  return store.getters['inboxes/getFilteredWhatsAppTemplates']?.(inboxId) ?? [];
});

const selectedTemplate = computed(() => props.modelValue?.template ?? null);

function getTemplateBody(template) {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text ?? '';
}

function getTemplateButtons(template) {
  return findComponentByType(template, COMPONENT_TYPES.BUTTONS);
}

const templateVariableCount = computed(() => {
  if (!selectedTemplate.value) return 0;
  const body = getTemplateBody(selectedTemplate.value);
  const nums = [...body.matchAll(/\{\{(\d+)\}\}/g)].map(m =>
    parseInt(m[1], 10)
  );
  return nums.length ? Math.max(...nums) : 0;
});

const templateVariableValues = computed(
  () => props.modelValue?.template_variables ?? []
);

async function selectTemplate(template) {
  let mediaUrl = '';
  const header =
    template && findComponentByType(template, COMPONENT_TYPES.HEADER);
  const format = header?.format?.toUpperCase();
  if (
    template &&
    format &&
    MEDIA_FORMATS.includes(format) &&
    props.modelValue?.inbox_id
  ) {
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${accountIdForApi.value}/inboxes/${props.modelValue.inbox_id}/whatsapp_templates/asset`,
        { params: { name: template.name, language: template.language } }
      );
      mediaUrl = data?.header_media_url ?? '';
    } catch (_e) {
      // sem asset cadastrado — segue sem pré-preencher
    }
  }
  update({
    template,
    template_variables: [],
    template_button_variables: {},
    template_header_media_url: mediaUrl,
  });
}

const templateHeaderFormat = computed(() => {
  if (!selectedTemplate.value) return null;
  const header = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.HEADER
  );
  if (!header) return null;
  const format = header.format?.toUpperCase();
  return MEDIA_FORMATS.includes(format) ? format : null;
});

const templateHeaderMediaUrl = computed({
  get: () => props.modelValue?.template_header_media_url ?? '',
  set: val => update({ template_header_media_url: val }),
});

const templateOptions = computed(() =>
  allTemplates.value.map(tpl => ({ id: tpl.name, name: tpl.name }))
);

const selectedTemplateOption = computed(
  () =>
    templateOptions.value.find(o => o.id === selectedTemplate.value?.name) ??
    null
);

function onSelectTemplateOption(val) {
  const template = allTemplates.value.find(tpl => tpl.name === val?.id);
  if (template) selectTemplate(template);
}

const showCreateTemplateModal = ref(false);

async function onTemplateCreated(template) {
  showCreateTemplateModal.value = false;
  try {
    await store.dispatch('inboxes/syncTemplates', props.modelValue?.inbox_id);
  } catch (_e) {
    // template recém-criado pode estar em PENDING; ignora erros de sync
  }
  const synced = allTemplates.value.find(tpl => tpl.name === template?.name);
  if (synced) selectTemplate(synced);
}

function setTemplateVariable(index, value) {
  const vars = [...(props.modelValue?.template_variables ?? [])];
  vars[index] = value;
  update({ template_variables: vars });
}

function varLabel(n) {
  return `{{${n}}}`;
}

// Template type detection
function hasButtonOfType(type) {
  if (!selectedTemplate.value) return false;
  const components = selectedTemplate.value.components ?? [];
  const buttonsComp = components.find(c => c.type?.toUpperCase() === 'BUTTONS');
  return (buttonsComp?.buttons ?? []).some(b => b.type?.toUpperCase() === type);
}

function hasComponentOfType(type) {
  if (!selectedTemplate.value) return false;
  return (selectedTemplate.value.components ?? []).some(
    c => c.type?.toUpperCase() === type
  );
}

function hasHeaderWithFormat(format) {
  if (!selectedTemplate.value) return false;
  const header = findComponentByType(selectedTemplate.value, COMPONENT_TYPES.HEADER);
  return header?.format?.toUpperCase() === format.toUpperCase();
}

const isSpmTemplate = computed(
  () =>
    hasButtonOfType('SPM') ||
    (hasHeaderWithFormat('PRODUCT') && !hasComponentOfType('CAROUSEL'))
);
const isMpmTemplate = computed(() => hasButtonOfType('MPM'));
const isCarouselTemplate = computed(() => hasComponentOfType('CAROUSEL'));
const needsProductConfig = computed(
  () => isSpmTemplate.value || isMpmTemplate.value || isCarouselTemplate.value
);

const productPickerMode = computed(() =>
  isSpmTemplate.value ? 'single' : 'multi'
);
const productPickerMin = computed(() => (isCarouselTemplate.value ? 2 : 1));
const productPickerMax = computed(() => (isCarouselTemplate.value ? 10 : 30));

const productConfig = computed({
  get: () =>
    props.modelValue?.template_product_config ?? {
      source: 'variable',
      variable_key: '',
      products: [],
    },
  set: val => update({ template_product_config: val }),
});

// Button URL variables
const buttonUrlVars = computed(() => {
  if (!selectedTemplate.value) return [];
  const buttonsComp = getTemplateButtons(selectedTemplate.value);
  if (!buttonsComp?.buttons) return [];
  const result = [];
  buttonsComp.buttons.forEach((btn, idx) => {
    if (btn.type === 'URL' && btn.url?.includes('{{')) {
      if (isSpmTemplate.value) return; // {{1}} auto-filled from product handle
      result.push({ buttonIndex: idx, buttonText: btn.text, url: btn.url });
    }
  });
  return result;
});

const templateButtonVariables = computed(
  () => props.modelValue?.template_button_variables ?? {}
);

function setButtonVariable(buttonIndex, value) {
  const vars = { ...templateButtonVariables.value, [buttonIndex]: value };
  update({ template_button_variables: vars });
}

const openPickerField = ref(null);
const templatePickerSearch = ref('');

const filteredTemplatePickerList = computed(() => {
  const q = templatePickerSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v =>
    resolveLabel(v.label).toLowerCase().includes(q)
  );
});

function toggleTemplatePicker(fieldId) {
  if (openPickerField.value === fieldId) {
    openPickerField.value = null;
    templatePickerSearch.value = '';
  } else {
    openPickerField.value = fieldId;
    templatePickerSearch.value = '';
  }
}

function insertTemplateVar(fieldId, key) {
  const wrapped = `{{${key}}}`;
  if (fieldId.startsWith('body-')) {
    const n = parseInt(fieldId.replace('body-', ''), 10);
    setTemplateVariable(n - 1, wrapped);
  } else if (fieldId.startsWith('btn-')) {
    const idx = parseInt(fieldId.replace('btn-', ''), 10);
    setButtonVariable(idx, wrapped);
  }
  openPickerField.value = null;
  templatePickerSearch.value = '';
}

// Audio recording
const isRecording = ref(false);
const isUploadingAudio = ref(false);
const recordingSeconds = ref(0);
let mediaRecorder = null;
let audioChunks = [];
let recordingInterval = null;
let activeStream = null;

function formatRecordingTime(s) {
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

async function startRecording() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    activeStream = stream;
    audioChunks = [];
    mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = e => {
      if (e.data.size > 0) audioChunks.push(e.data);
    };
    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(track => track.stop());
      recordingSeconds.value = 0;
      clearInterval(recordingInterval);
      const blob = new Blob(audioChunks, { type: 'audio/webm' });
      const file = new File([blob], `audio-${Date.now()}.webm`, {
        type: 'audio/webm',
      });
      isUploadingAudio.value = true;
      try {
        const { fileUrl, blobId } = await uploadFile(file);
        update({
          attachments: [
            ...attachments.value,
            { type: 'audio', name: file.name, url: fileUrl, blobId },
          ],
        });
      } finally {
        isUploadingAudio.value = false;
      }
    };
    mediaRecorder.start();
    isRecording.value = true;
    recordingInterval = setInterval(() => {
      recordingSeconds.value += 1;
    }, 1000);
  } catch {
    // microphone access denied or unavailable
  }
}

function stopRecording() {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
    isRecording.value = false;
  }
}

onBeforeUnmount(() => {
  if (isRecording.value) {
    clearInterval(recordingInterval);
    activeStream?.getTracks().forEach(track => track.stop());
  }
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Channel type -->
    <div class="multiselect-wrap--small">
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.CHANNEL_TYPE')
        }}</span>
      </div>
      <MultiselectDropdown
        :options="CHANNEL_OPTIONS"
        :selected-item="selectedChannelType"
        :multiselector-placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.ALL_CHANNELS')
        "
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        hide-search
        @select="onSelectChannelType"
      />
    </div>

    <!-- Inbox -->
    <div class="multiselect-wrap--small">
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.CHANNEL_INBOX')
        }}</span>
      </div>
      <MultiselectDropdown
        :options="filteredInboxOptions"
        :selected-item="selectedInbox"
        :multiselector-placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.SELECT_CHANNEL')
        "
        :input-placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.SEARCH_CHANNEL')
        "
        :has-thumbnail="false"
        @select="onSelectInbox"
      />
    </div>

    <!-- Message type -->
    <div class="multiselect-wrap--small">
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MESSAGE_TYPE')
        }}</span>
      </div>
      <MultiselectDropdown
        :options="MESSAGE_TYPE_OPTIONS"
        :selected-item="selectedMessageType"
        :multiselector-placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.SELECT_TYPE')
        "
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        hide-search
        @select="onSelectMessageType"
      />
    </div>

    <!-- Manual: editor + attachments + buttons -->
    <template v-if="messageType === 'manual'">
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">{{
          t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MESSAGE')
        }}</label>
        <div
          class="bg-n-solid-1 rounded-lg outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
        >
          <WootMessageEditor
            ref="editorRef"
            v-model="content"
            :editor-id="editorId"
            enable-variables
            :enable-canned-responses="false"
            :enable-suggestions="false"
            :enable-copilot="false"
            :focus-on-mount="false"
            :variables="FLOW_VARIABLES"
            :variable-labels="VARIABLE_LABELS"
            :placeholder="
              t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MESSAGE_PLACEHOLDER')
            "
            class="px-3 pt-2 pb-1 min-h-[100px] text-sm"
          />
          <div
            class="flex items-center gap-0.5 px-2 py-1 border-t border-n-weak"
          >
            <div
              v-on-click-outside="() => (showEmojiPicker = false)"
              class="relative"
            >
              <button
                type="button"
                class="inline-flex items-center justify-center size-7 rounded-lg text-n-slate-11 hover:bg-n-slate-9/10 transition-colors"
                @click="showEmojiPicker = !showEmojiPicker"
              >
                <span class="i-ph-smiley-sticker size-4 flex-shrink-0" />
              </button>
              <EmojiInput
                v-if="showEmojiPicker"
                class="absolute bottom-full mb-1 ltr:left-0 rtl:right-0 z-50"
                :on-click="onEmojiClick"
              />
            </div>
            <label
              class="inline-flex items-center justify-center size-7 rounded-lg text-n-slate-11 hover:bg-n-slate-9/10 transition-colors cursor-pointer"
            >
              <span class="i-ph-paperclip size-4" />
              <input
                type="file"
                class="hidden"
                @change="handleFileInput($event, 'file')"
              />
            </label>
            <div
              v-if="isRecording"
              class="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-n-ruby-3"
            >
              <span
                class="size-1.5 rounded-full bg-n-ruby-9 animate-pulse shrink-0"
              />
              <span class="text-xs font-mono text-n-ruby-11 tabular-nums">{{
                formatRecordingTime(recordingSeconds)
              }}</span>
              <button
                type="button"
                class="inline-flex items-center justify-center size-5 rounded text-n-ruby-11 hover:bg-n-ruby-4 transition-colors"
                @click="stopRecording"
              >
                <span class="i-lucide-square size-3 shrink-0" />
              </button>
            </div>
            <div
              v-else-if="isUploadingAudio"
              class="flex items-center gap-1 px-2 py-0.5"
            >
              <span
                class="i-lucide-loader-circle size-3.5 text-n-slate-9 animate-spin shrink-0"
              />
              <span class="text-xs text-n-slate-9">{{
                t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.UPLOADING')
              }}</span>
            </div>
            <button
              v-else
              type="button"
              class="inline-flex items-center justify-center size-7 rounded-lg text-n-slate-11 hover:bg-n-slate-9/10 transition-colors"
              @click="startRecording"
            >
              <span class="i-ph-microphone size-4 flex-shrink-0" />
            </button>
            <div
              v-on-click-outside="
                () => {
                  showVariablePicker = false;
                  variableSearch = '';
                }
              "
              class="relative ml-auto"
            >
              <button
                type="button"
                class="inline-flex items-center justify-center size-7 rounded-lg text-n-slate-11 hover:bg-n-slate-9/10 transition-colors"
                :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
                @click="showVariablePicker = !showVariablePicker"
              >
                <span class="i-ph-brackets-curly size-4 flex-shrink-0" />
              </button>
              <div
                v-if="showVariablePicker"
                class="absolute bottom-full mb-1 right-0 z-50 bg-n-solid-2 border border-n-strong rounded-lg shadow-md w-[200px] overflow-hidden"
              >
                <div class="px-2 pt-2 pb-1">
                  <div
                    class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
                  >
                    <span
                      class="i-lucide-search size-3 text-n-slate-9 shrink-0"
                    />
                    <input
                      v-model="variableSearch"
                      type="text"
                      class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                      :placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                      autofocus
                    />
                  </div>
                </div>
                <div
                  class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom"
                >
                  <button
                    v-for="attr in filteredAttrList"
                    :key="attr.key"
                    type="button"
                    class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                    @click="insertVariable(attr.key)"
                  >
                    {{ resolveLabel(attr.label) }}
                  </button>
                  <p
                    v-if="!filteredAttrList.length"
                    class="px-3 py-2 text-xs text-n-slate-9 italic"
                  >
                    {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Attachment list -->
      <div v-if="attachments.length" class="flex flex-col gap-1.5">
        <div
          v-for="(att, i) in attachments"
          :key="i"
          class="flex items-center justify-between px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="size-4 shrink-0"
              :class="{
                'i-lucide-image': att.type === 'image',
                'i-ph-microphone': att.type === 'audio',
                'i-ph-paperclip': att.type === 'file',
              }"
            />
            <span class="truncate text-n-slate-11 text-xs">{{ att.name }}</span>
          </div>
          <button
            type="button"
            class="i-lucide-x size-3.5 text-n-slate-9 hover:text-n-ruby-11 shrink-0 ml-2"
            @click="removeAttachment(i)"
          />
        </div>
      </div>
    </template>

    <!-- Template picker -->
    <template v-else-if="messageType === 'template'">
      <!-- Template select -->
      <div class="multiselect-wrap--small">
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-sm font-medium text-n-slate-12">{{
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.TEMPLATE')
          }}</span>
          <button
            type="button"
            class="flex items-center gap-1 text-xs text-n-blue-11 hover:underline disabled:opacity-50"
            :disabled="!modelValue?.inbox_id"
            @click="showCreateTemplateModal = true"
          >
            <span class="i-lucide-plus size-3" />
            {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.CREATE_TEMPLATE') }}
          </button>
        </div>
        <MultiselectDropdown
          :options="templateOptions"
          :selected-item="selectedTemplateOption"
          :multiselector-placeholder="
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.SELECT_TEMPLATE')
          "
          :input-placeholder="
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.SEARCH_TEMPLATE')
          "
          :has-thumbnail="false"
          :disabled="!modelValue?.inbox_id"
          @select="onSelectTemplateOption"
        />
      </div>

      <CreateTemplateModal
        :show="showCreateTemplateModal"
        :inbox-id="modelValue?.inbox_id"
        :flow-slug="flowSlug"
        @close="showCreateTemplateModal = false"
        @created="onTemplateCreated"
      />

      <!-- Product picker — SPM, MPM, CAROUSEL -->
      <div v-if="needsProductConfig" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCTS_LABEL') }}
        </span>
        <TemplateProductPicker
          :model-value="productConfig"
          :mode="productPickerMode"
          :min="productPickerMin"
          :max="productPickerMax"
          :available-variables="availableVariables"
          :resolve-label="resolveLabel"
          @update:model-value="productConfig = $event"
        />
      </div>

      <!-- Media header URL -->
      <div v-if="templateHeaderFormat" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MEDIA_URL') }}
          <span class="font-normal text-n-slate-9">{{
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MEDIA_FORMAT', {
              format: templateHeaderFormat.toLowerCase(),
            })
          }}</span>
        </span>
        <input
          v-model="templateHeaderMediaUrl"
          type="url"
          class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 transition-all"
          :placeholder="
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.MEDIA_URL_PLACEHOLDER')
          "
        />
      </div>

      <!-- Body variables -->
      <template v-if="selectedTemplate && templateVariableCount > 0">
        <div
          v-for="n in templateVariableCount"
          :key="`body-${n}`"
          class="flex flex-col gap-1.5"
        >
          <span class="text-sm font-medium text-n-slate-12">{{
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.VARIABLE_LABEL', {
              name: varLabel(n),
            })
          }}</span>
          <div class="relative">
            <div
              class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
            >
              <input
                type="text"
                class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :value="templateVariableValues[n - 1] ?? ''"
                :placeholder="
                  t(
                    'FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.VALUE_OR_VARIABLE_PLACEHOLDER'
                  )
                "
                @input="setTemplateVariable(n - 1, $event.target.value)"
              />
              <button
                type="button"
                class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
                @click.stop="toggleTemplatePicker(`body-${n}`)"
              >
                <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
              </button>
            </div>
            <div
              v-if="openPickerField === `body-${n}`"
              class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
            >
              <div class="px-2 pt-2 pb-1">
                <div
                  class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
                >
                  <span
                    class="i-lucide-search size-3 text-n-slate-9 shrink-0"
                  />
                  <input
                    v-model="templatePickerSearch"
                    type="text"
                    class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                    :placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                    autofocus
                  />
                </div>
              </div>
              <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                <button
                  v-for="v in filteredTemplatePickerList"
                  :key="v.key"
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  @click="insertTemplateVar(`body-${n}`, v.key)"
                >
                  {{ resolveLabel(v.label) }}
                </button>
                <p
                  v-if="!filteredTemplatePickerList.length"
                  class="px-3 py-2 text-xs text-n-slate-9 italic"
                >
                  {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Button URL variables -->
      <template v-if="selectedTemplate && buttonUrlVars.length">
        <div
          v-for="btnVar in buttonUrlVars"
          :key="`btn-${btnVar.buttonIndex}`"
          class="flex flex-col gap-1.5"
        >
          <div>
            <span class="text-sm font-medium text-n-slate-12">{{
              btnVar.buttonText
            }}</span>
            <span class="block text-xs text-n-slate-9 font-mono">{{
              btnVar.url
            }}</span>
          </div>
          <div class="relative">
            <div
              class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
            >
              <input
                type="text"
                class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :value="templateButtonVariables[btnVar.buttonIndex] ?? ''"
                :placeholder="
                  t(
                    'FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.VALUE_OR_VARIABLE_PLACEHOLDER'
                  )
                "
                @input="
                  setButtonVariable(btnVar.buttonIndex, $event.target.value)
                "
              />
              <button
                type="button"
                class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
                @click.stop="toggleTemplatePicker(`btn-${btnVar.buttonIndex}`)"
              >
                <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
              </button>
            </div>
            <div
              v-if="openPickerField === `btn-${btnVar.buttonIndex}`"
              class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
            >
              <div class="px-2 pt-2 pb-1">
                <div
                  class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
                >
                  <span
                    class="i-lucide-search size-3 text-n-slate-9 shrink-0"
                  />
                  <input
                    v-model="templatePickerSearch"
                    type="text"
                    class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                    :placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                    autofocus
                  />
                </div>
              </div>
              <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                <button
                  v-for="v in filteredTemplatePickerList"
                  :key="v.key"
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  @click="insertTemplateVar(`btn-${btnVar.buttonIndex}`, v.key)"
                >
                  {{ resolveLabel(v.label) }}
                </button>
                <p
                  v-if="!filteredTemplatePickerList.length"
                  class="px-3 py-2 text-xs text-n-slate-9 italic"
                >
                  {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
